import type {ButtonVariant, MantineColor} from '@mantine/core';
import React from 'react';
import {
  AddIcon,
  CloseIcon,
  CopyIcon,
  DeleteIcon,
  EditIcon,
  ExportIcon,
  ImportIcon,
  SaveIcon,
} from 'src/design/atoms/icons/CommonIcons';

/**
 * Shared action variants and styling lookup used by PageHeader and TableToolbar.
 * @registry Maps semantic action names (edit/save/delete/etc.) to button
 * variant, color and default icon/text.
 *
 * Ported from `library-m/utils/actionConfig`. Exported names
 * (`ActionVariant`, `ActionConfig`, `getActionConfig`) are unchanged so
 * migrating call sites is mechanical, but the mapping itself may diverge
 * from the original (e.g. `secondary` uses `outline`, not `light` — light
 * variant buttons are banned by the design system).
 */
export type ActionVariant =
  | 'primary'
  | 'secondary'
  | 'cancel'
  | 'danger'
  // Semantic variants with built-in icons
  | 'edit'
  | 'save'
  | 'create'
  | 'delete'
  | 'clone'
  | 'refresh'
  | 'close'
  | 'export'
  | 'import';

export interface ActionConfig {
  buttonVariant: ButtonVariant;
  color: MantineColor;
  icon: React.ReactNode | null;
  text?: string;
}

/**
 * Get button styling and icon for an action variant.
 * Used by PageHeader.Action and TableToolbar.Action.
 */
export const getActionConfig = (variant: ActionVariant): ActionConfig => {
  switch (variant) {
    // Style-only variants (no built-in icon)
    case 'primary':
      return {buttonVariant: 'filled', color: 'yellow', icon: null};
    case 'secondary':
      return {buttonVariant: 'outline', color: 'yellow', icon: null};
    case 'cancel':
      return {buttonVariant: 'subtle', color: 'gray', icon: null};
    case 'danger':
      return {buttonVariant: 'filled', color: 'red', icon: null};

    // Semantic variants with built-in icons and default text
    case 'edit':
      return {
        buttonVariant: 'filled',
        color: 'yellow',
        icon: <EditIcon />,
        text: 'Edit',
      };
    case 'save':
      return {
        buttonVariant: 'filled',
        color: 'yellow',
        icon: <SaveIcon />,
        text: 'Save',
      };
    case 'create':
      return {
        buttonVariant: 'filled',
        color: 'yellow',
        icon: <AddIcon />,
        text: 'Create',
      };
    case 'delete':
      return {
        buttonVariant: 'filled',
        color: 'red',
        icon: <DeleteIcon />,
        text: 'Delete',
      };
    case 'clone':
      return {
        buttonVariant: 'filled',
        color: 'yellow',
        icon: <CopyIcon />,
        text: 'Clone',
      };
    case 'close':
      return {
        buttonVariant: 'subtle',
        color: 'black',
        icon: <CloseIcon />,
        text: 'Close',
      };
    case 'export':
      return {
        buttonVariant: 'subtle',
        color: 'black',
        icon: <ExportIcon />,
        text: 'Export',
      };
    case 'import':
      return {
        buttonVariant: 'subtle',
        color: 'black',
        icon: <ImportIcon />,
        text: 'Import',
      };

    default:
      return {buttonVariant: 'filled', color: 'yellow', icon: null};
  }
};
